import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import Parse from 'html-react-parser';
import {
  Button,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { APIgetFilePreview } from '../../api/Files';

import { getAPIUrl } from '../../helpers/App';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';

import ErrorBoundary from '../ErrorBoundary';

class AdminLogoUploader extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      uploading: false,
      uploaded: 0,
      error: null,
    };

    this.fileInput = React.createRef();
    this.initUploader();

    this.uploadFile = this.uploadFile.bind(this);
    this.triggerInputFile = this.triggerInputFile.bind(this);
  }

  componentDidMount() {
    this.uploader.on('submitted', (id) => {
      const { loggedInProfile } = this.props;
      this.setState({
        uploading: true,
        error: null,
      });
      this.uploader.methods.setParams({
        client: loggedInProfile.client.uuid,
      }, id);
    });

    this.uploader.on('progress', (id, name, uploadedBytes, totalBytes) => {
      let uploaded = 0;
      if (uploadedBytes > 0) {
        uploaded = window._.round((uploadedBytes / totalBytes) * 100, 0);
      }
      this.setState({ uploaded });
    });

    this.uploader.on('complete', (id, name, response) => {
      if (!response.success) {
        return;
      }

      const { previewOriginal } = this.props;
      const previewParams = previewOriginal ? { original: true } : { full: true };

      APIgetFilePreview(this.uploader.methods.getKey(id), previewParams).then((res) => {
        if (res.data && res.data.url) {
          this.handleChangePreview(res.data.url);
        }
        this.setState({
          uploading: false,
          uploaded: 0,
        });
      });
      this.handleChangeImage(this.uploader.methods.getKey(id), name);
    });

    this.uploader.on('error', (id, name, error) => {
      const { intl } = this.props;
      this.setState({
        uploading: false,
        uploaded: 0,
        error,
      });
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({
          defaultMessage: '<strong>Something went wrong.</strong>{br}Please check for error messages.',
        }, { br: '\n' }),
      });
    });
  }

  initUploader() {
    const { intl, path } = this.props;

    this.uploader = new FineUploaderS3({
      options: {
        debug: process.env.MIX_ENV !== 'production',
        multiple: false,
        request: {
          endpoint: `https://${process.env.MIX_AWS_S3_BUCKET_NAME}.s3.amazonaws.com`,
          accessKey: `${getSetting('upload_key') || process.env.MIX_AWS_ACCESS_KEY_ID}`,
        },
        objectProperties: {
          key: (id) => {
            const { loggedInProfile } = this.props;
            const ext = this.uploader.qq.getExtension(this.uploader.methods.getName(id));
            const uuid = this.uploader.methods.getUuid(id);
            return `${loggedInProfile.client.uuid}/${path}/${uuid}.${ext}`;
          },
        },
        validation: {
          image: {
            minWidth: 200,
          },
        },
        messages: {
          minWidthImageError: intl.formatMessage({ defaultMessage: 'Image is too small - minimum width is 200 px' }),
        },
        signature: {
          endpoint: getAPIUrl('/upload'),
          version: 4,
          customHeaders: () => ({
            SPAuth: true,
            Authorization: `Bearer ${Cookies.get('sp_access_token')}`,
          }),
        },
        chunking: {
          enabled: true,
          concurrent: {
            enabled: true,
          },
        },
      },
    });
  }

  uploadFile(e) {
    const { files } = e.target;
    if (files.length > 0) {
      this.uploader.methods.addFiles(files);
    }
  }

  triggerInputFile() {
    const { uploading } = this.state;
    if (!uploading) {
      this.fileInput.current.click();
    }
  }

  handleChangeImage(imageUrl, originalFileName = '') {
    const { handleChangeImage } = this.props;
    handleChangeImage(imageUrl, originalFileName);
  }

  handleChangePreview(imageUrl) {
    const { handleChangePreview } = this.props;
    handleChangePreview(imageUrl);
  }

  render() {
    const {
      uploading,
      uploaded,
      error,
    } = this.state;
    const {
      intl,
      name,
      uploadButtonIcon,
      uploadButtonText,
      suffix,
      accept,
    } = this.props;

    return (
      <ErrorBoundary>
        {error && (
          <span style={{ display: 'block' }} className="invalid-feedback">
            {Parse(error)}
          </span>
        )}
        <Button
          className="logo-uploader-button"
          color="link"
          onClick={this.triggerInputFile}
          id={`LogoUploader-${suffix}`}
        >
          {uploading ?
            <i className="far fa-spinner fa-spin fa-fw" /> :
            <i className={`far fa-fw ${uploadButtonIcon}`} />}
        </Button>
        <UncontrolledTooltip
          target={`LogoUploader-${suffix}`}
          placement="top"
        >
          {uploading ? (`${uploaded}%`) : (uploadButtonText || intl.formatMessage({ defaultMessage: 'Change' }))}
        </UncontrolledTooltip>
        <Input
          type="file"
          name={name}
          accept={accept}
          className="upload-file"
          onChange={this.uploadFile}
          innerRef={this.fileInput}
        />
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  loggedInProfile: state.Auth.profile,
});

AdminLogoUploader.propTypes = {
  intl: PropTypes.object.isRequired,
  loggedInProfile: PropTypes.object.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleChangePreview: PropTypes.func.isRequired,
  path: PropTypes.string,
  name: PropTypes.string,
  uploadButtonIcon: PropTypes.string,
  uploadButtonText: PropTypes.string,
  suffix: PropTypes.string,
  accept: PropTypes.string,
  previewOriginal: PropTypes.bool,
};

AdminLogoUploader.defaultProps = {
  path: 'logo',
  name: 'logo-uploader',
  uploadButtonIcon: 'fa-random',
  uploadButtonText: '',
  suffix: '',
  accept: '.png, .jpg, .jpeg',
  previewOriginal: false,
};

export default connect(mapStateToProps)(injectIntl(AdminLogoUploader));
